import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { CircularProgress } from '@mui/material';

import type { VerificationStatus } from 'tools/constants/payment';

export interface StatusConfig {
  ariaLabel: string;
  description: string;
  footer: FooterType;
  icon: React.ReactNode;
  title: string;
}

export const enum FooterType {
  REDIRECT = 'redirect',
  RETRY = 'retry',
  LOADING = 'loading',
  NONE = 'none',
}

export enum VerificationFlow {
  TRANSACTION = 'transaction',
  TRANSACTION_NO_REDIRECT = 'transactionNoRedirect',
  ADD_PAYMENT_METHOD = 'addPaymentMethod',
  PAYMENT_LINK_TRANSACTION = 'paymentLinkTransaction',
}

const TRANSACTION_STATUS_CONFIG: Record<VerificationStatus, StatusConfig> = {
  cancel: {
    ariaLabel: 'Payment canceled',
    description: '',
    footer: FooterType.REDIRECT,
    icon: (
      <CancelOutlinedIcon
        aria-label="Payment canceled"
        sx={{ color: 'text.secondary', fontSize: 64 }}
      />
    ),
    title: 'Payment Canceled',
  },
  error: {
    ariaLabel: 'Payment failed',
    description: '',
    footer: FooterType.REDIRECT,
    icon: (
      <ErrorOutlineIcon
        aria-label="Payment failed"
        color="error"
        sx={{ fontSize: 64 }}
      />
    ),
    title: 'Payment Failed',
  },
  invalid: {
    ariaLabel: 'An error occurred',
    description:
      'One or more required parameters are missing or invalid. Please return to the payment page and try again.',
    footer: FooterType.RETRY,
    icon: (
      <ErrorOutlineIcon
        aria-label="An error occurred"
        color="error"
        sx={{ fontSize: 64 }}
      />
    ),
    title: 'Something Went Wrong',
  },
  loading: {
    ariaLabel: 'Verifying payment',
    description:
      "We're confirming your payment status. This may take a moment.",
    footer: FooterType.LOADING,
    icon: (
      <CircularProgress
        aria-label="Verifying payment"
        size={56}
        thickness={3.5}
      />
    ),
    title: 'Verifying Payment',
  },
  success: {
    ariaLabel: 'Payment successful',
    description: '',
    footer: FooterType.REDIRECT,
    icon: (
      <CheckCircleOutlineIcon
        aria-label="Payment successful"
        color="success"
        sx={{ fontSize: 64 }}
      />
    ),
    title: 'Payment Successful',
  },
  timeout: {
    ariaLabel: 'Verification timed out',
    description: '',
    footer: FooterType.REDIRECT,
    icon: (
      <HelpOutlineIcon
        aria-label="Verification timed out"
        color="warning"
        sx={{ fontSize: 64 }}
      />
    ),
    title: 'Payment Verification Timed Out',
  },
};

const ADD_PAYMENT_METHOD_STATUS_CONFIG: Record<
  VerificationStatus,
  StatusConfig
> = {
  cancel: {
    ariaLabel: 'Payment method addition canceled',
    description: '',
    footer: FooterType.REDIRECT,
    icon: (
      <CancelOutlinedIcon
        aria-label="Payment method addition canceled"
        sx={{ color: 'text.secondary', fontSize: 64 }}
      />
    ),
    title: 'Payment Method Not Added',
  },
  error: {
    ariaLabel: 'Failed to add payment method',
    description: '',
    footer: FooterType.REDIRECT,
    icon: (
      <ErrorOutlineIcon
        aria-label="Failed to add payment method"
        color="error"
        sx={{ fontSize: 64 }}
      />
    ),
    title: 'Failed to Add Payment Method',
  },
  invalid: {
    ariaLabel: 'An error occurred',
    description:
      'One or more required parameters are missing or invalid. Please return to the previous page and try again.',
    footer: FooterType.RETRY,
    icon: (
      <ErrorOutlineIcon
        aria-label="An error occurred"
        color="error"
        sx={{ fontSize: 64 }}
      />
    ),
    title: 'Something Went Wrong',
  },
  loading: {
    ariaLabel: 'Verifying payment method',
    description:
      "We're confirming your payment method. This may take a moment.",
    footer: FooterType.LOADING,
    icon: (
      <CircularProgress
        aria-label="Verifying payment method"
        size={56}
        thickness={3.5}
      />
    ),
    title: 'Verifying Payment Method',
  },
  success: {
    ariaLabel: 'Payment method added',
    description: '',
    footer: FooterType.REDIRECT,
    icon: (
      <CheckCircleOutlineIcon
        aria-label="Payment method added"
        color="success"
        sx={{ fontSize: 64 }}
      />
    ),
    title: 'Payment Method Added',
  },
  timeout: {
    ariaLabel: 'Verification timed out',
    description: '',
    footer: FooterType.REDIRECT,
    icon: (
      <HelpOutlineIcon
        aria-label="Verification timed out"
        color="warning"
        sx={{ fontSize: 64 }}
      />
    ),
    title: 'Verification Timed Out',
  },
};

export const TRANSACTION_DESCRIPTION_MAP: Record<string, string> = {
  cancel: 'You canceled the payment.',
  error: 'Your payment could not be processed.',
  success: 'Your payment has been confirmed.',
  timeout: 'Your payment verification timed out.',
};

export const ADD_PAYMENT_METHOD_DESCRIPTION_MAP: Record<string, string> = {
  cancel: 'You canceled adding the payment method.',
  error: 'Your payment method could not be added.',
  success: 'Your payment method has been added successfully.',
  timeout: 'Payment method verification timed out.',
};

const PAYMENT_LINK_TRANSACTION_STATUS_CONFIG: Record<
  VerificationStatus,
  StatusConfig
> = {
  cancel: {
    ...TRANSACTION_STATUS_CONFIG.cancel,
    description: TRANSACTION_DESCRIPTION_MAP.cancel,
    footer: FooterType.NONE,
  },
  error: {
    ...TRANSACTION_STATUS_CONFIG.error,
    description: TRANSACTION_DESCRIPTION_MAP.error,
    footer: FooterType.NONE,
  },
  invalid: {
    ...TRANSACTION_STATUS_CONFIG.invalid,
    footer: FooterType.NONE,
  },
  loading: TRANSACTION_STATUS_CONFIG.loading,
  success: {
    ...TRANSACTION_STATUS_CONFIG.success,
    description: TRANSACTION_DESCRIPTION_MAP.success,
    footer: FooterType.NONE,
  },
  timeout: {
    ...TRANSACTION_STATUS_CONFIG.timeout,
    description: TRANSACTION_DESCRIPTION_MAP.timeout,
    footer: FooterType.NONE,
  },
};

const STATUS_CONFIG_MAP: Record<
  VerificationFlow,
  Record<VerificationStatus, StatusConfig>
> = {
  [VerificationFlow.TRANSACTION]: TRANSACTION_STATUS_CONFIG,
  [VerificationFlow.TRANSACTION_NO_REDIRECT]:
    PAYMENT_LINK_TRANSACTION_STATUS_CONFIG,
  [VerificationFlow.ADD_PAYMENT_METHOD]: ADD_PAYMENT_METHOD_STATUS_CONFIG,
  [VerificationFlow.PAYMENT_LINK_TRANSACTION]:
    PAYMENT_LINK_TRANSACTION_STATUS_CONFIG,
};

const DESCRIPTION_MAP_BY_FLOW: Record<
  VerificationFlow,
  Record<string, string>
> = {
  [VerificationFlow.TRANSACTION]: TRANSACTION_DESCRIPTION_MAP,
  [VerificationFlow.TRANSACTION_NO_REDIRECT]: TRANSACTION_DESCRIPTION_MAP,
  [VerificationFlow.ADD_PAYMENT_METHOD]: ADD_PAYMENT_METHOD_DESCRIPTION_MAP,
  [VerificationFlow.PAYMENT_LINK_TRANSACTION]: TRANSACTION_DESCRIPTION_MAP,
};

export const getStatusConfig = (
  flow: VerificationFlow,
): Record<VerificationStatus, StatusConfig> => STATUS_CONFIG_MAP[flow];

export const getDescriptionMap = (
  flow: VerificationFlow,
): Record<string, string> => DESCRIPTION_MAP_BY_FLOW[flow];
